import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDay } from "@/lib/time";
import { gbp } from "@/lib/stages";
import { CellMain, Panel, St, Tb, Td, Th } from "@/components/portal/crm/kit";

const num = { fontVariantNumeric: "tabular-nums" };
/* The "First funded case" note stays on a seat's row for 30 days, then the figures speak for themselves. */
const recentFirst = (iso) => !!iso && (Date.now() - new Date(iso).getTime()) < 30 * 86400000;

/** "3 of 10" + a bar; click the target to set or change it. */
function TargetCell({ row, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.monthly_target ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(row.monthly_target ?? ""); }, [row.monthly_target]);

  const save = async () => {
    const n = val === "" ? 0 : Math.max(0, Math.min(10000, parseInt(val, 10) || 0));
    setBusy(true);
    try { await onSave(row, n); setEditing(false); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); save(); }} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6 }}
            data-testid={`referrer-seat-target-form-${row.id}`}>
        <input type="number" min="0" max="10000" value={val} onChange={(e) => setVal(e.target.value)} autoFocus
               placeholder="0" aria-label="Monthly case target" className="inp" style={{ width: 64, height: 30, padding: "0 8px", textAlign: "right", ...num }}
               data-testid={`referrer-seat-target-input-${row.id}`} />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy} data-testid={`referrer-seat-target-save-${row.id}`}>Set</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setVal(row.monthly_target ?? ""); }} data-testid={`referrer-seat-target-cancel-${row.id}`}>Cancel</button>
      </form>
    );
  }

  const t = row.monthly_target;
  const pct = t ? Math.min(100, Math.round((100 * row.this_month) / t)) : 0;
  const tone = !t ? "var(--line)" : pct >= 100 ? "var(--green)" : pct >= 50 ? "var(--accent)" : "var(--amber)";
  return (
    <button type="button" onClick={() => setEditing(true)} data-testid={`referrer-seat-target-${row.id}`}
            title={t ? "Change this month's target" : "Set a monthly case target"}
            style={{ border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer", textAlign: "right", width: "100%", minWidth: 0 }}>
      <CellMain style={{ ...num, color: t ? "var(--ink)" : "var(--mut)", fontWeight: t ? 700 : 500 }}>
        {row.this_month}{t ? <span style={{ color: "var(--mut)", fontWeight: 500 }}> / {t}</span> : <span style={{ color: "var(--mut)", fontWeight: 500 }}> · set target</span>}
      </CellMain>
      <div aria-hidden style={{ height: 4, borderRadius: 999, background: "var(--line-soft, #eef1f6)", marginTop: 6, overflow: "hidden" }}>
        <div style={{ width: `${t ? pct : 0}%`, height: "100%", background: tone, borderRadius: 999, transition: "width .4s ease" }} data-testid={`referrer-seat-target-bar-${row.id}`} />
      </div>
    </button>
  );
}

/** Who's bringing the cases — one row per seat plus the account holder, ranked on funded → live → cases.
    Every cell is a single line so the figures sit on one baseline across the row. */
export function SeatLeaderboard({ refreshKey }) {
  const [board, setBoard] = useState(null);

  const load = () => api.get("/refer/seats/leaderboard")
    .then((r) => setBoard(r.data))
    .catch(() => setBoard({ rows: [] }));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTarget = async (row, n) => {
    try {
      if (row.you) await api.patch("/refer/seats/holder-target", { monthly_target: n });
      else await api.patch(`/refer/seats/${row.id}`, { monthly_target: n });
      toast.success(n ? `Target set — ${n} case${n === 1 ? "" : "s"} a month for ${row.name}` : `Target cleared for ${row.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the target"); throw e; }
  };

  const rows = board?.rows || [];
  if (rows.length < 2) return null;
  const targets = board.targets_set || 0;

  return (
    <Panel title="Who's bringing the cases" pad={false} testId="referrer-seat-board"
           sub={`${board.seat_cases} of ${board.total_cases} case${board.total_cases === 1 ? "" : "s"} came in through your seats (${board.seat_share_pct}%). Ranked on funded, then live, then cases in.${targets ? ` ${board.on_track} of ${targets} target${targets === 1 ? "" : "s"} hit so far in ${board.month_label}.` : ""}`}
           footer={<span>Every case here earns your company's commission, whoever put it in. Click a figure in the <b>{board.month_label}</b> column to set that person's monthly target — the bar fills as their cases come in. A removed seat stays on the board while its cases do.</span>}>
      <Tb testId="referrer-seat-board-table" head={<>
        <Th style={{ width: "4%" }}>#</Th>
        <Th style={{ width: "28%" }}>Person</Th>
        <Th className="r" style={{ width: "15%" }}>{board.month_label}</Th>
        <Th className="r" style={{ width: "9%" }}>Cases in</Th>
        <Th className="r" style={{ width: "9%" }}>Declined</Th>
        <Th className="r" style={{ width: "8%" }}>Live</Th>
        <Th className="r" style={{ width: "8%" }}>Funded</Th>
        <Th className="r" style={{ width: "9%" }}>Written</Th>
        <Th className="r" style={{ width: "10%" }}>Last case</Th>
      </>}>
        {rows.map((r) => (
          <tr key={r.id} data-testid={`referrer-seat-board-row-${r.id}`}>
            <Td><span style={{ ...num, fontSize: 12.5, fontWeight: 600, color: "var(--mut)" }}>{r.rank}</span></Td>
            <Td className="lead">
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <CellMain style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</CellMain>
                {r.you && <St tone="ink" testId="referrer-seat-board-you">You</St>}
                {!r.you && r.status === "paused" && <St tone="slate">Paused</St>}
                {!r.you && r.status === "removed" && <St tone="slate">Removed</St>}
                {!r.you && r.status === "active" && recentFirst(r.first_funded_at) && (
                  <St tone="green" testId={`referrer-seat-board-first-funded-${r.id}`} style={{ whiteSpace: "nowrap" }}>First funded case</St>
                )}
              </div>
            </Td>
            <Td className="r">
              {r.status === "removed" ? <CellMain style={{ ...num, color: "var(--mut)", fontWeight: 500 }}>{r.this_month}</CellMain> : <TargetCell row={r} onSave={saveTarget} />}
            </Td>
            <Td className="r" data-testid={`referrer-seat-board-cases-${r.id}`}><CellMain style={num}>{r.cases}</CellMain></Td>
            <Td className="r"><CellMain style={{ ...num, color: r.declined ? "var(--ink)" : "var(--mut)", fontWeight: r.declined ? 700 : 500 }}>{r.declined}</CellMain></Td>
            <Td className="r"><CellMain style={num}>{r.live}</CellMain></Td>
            <Td className="r"><CellMain style={{ ...num, color: r.funded ? "var(--green)" : undefined }}>{r.funded}</CellMain></Td>
            <Td className="r"><CellMain style={{ ...num, color: r.funded ? "var(--ink)" : "var(--mut)", fontWeight: r.funded ? 700 : 500 }}>{r.funded ? gbp(r.funded_gbp, { compact: true }) : "—"}</CellMain></Td>
            <Td className="r"><CellMain style={{ ...num, color: "var(--mut)", fontWeight: 500 }}>{r.last_case_at ? ukDay(r.last_case_at) : "—"}</CellMain></Td>
          </tr>
        ))}
      </Tb>
    </Panel>
  );
}
